<template>
  <div :class="`mytable ${className}`">
    <div v-if="showHeader" class="header">
      <div class="page-title">
        <slot name="pageTitle" :title="TableTitle">{{ TableTitle }}</slot>
      </div>
      <div v-if="globalBtns.length > 0" class="mytable-buttons">
        <el-button
          v-for="btn in globalBtns"
          :key="btn.name"
          :data-cy="btn.name"
          :type="btn.type"
          v-debounce-click="() => handleGlobalButton(btn)"
          >{{ btn.label }}</el-button
        >
      </div>
    </div>
    <div v-if="searchCols.length > 0" class="search-bar">
      <search-form
        :searchParams="searchParams"
        :search-cols="searchCols"
        @search="onSearch"
        :default-show-more="cfg.defaultShowMore"
        @error="loading = false"
      />
      <div class="global-icons">
        <template v-if="!showHeader">
          <loading-icon
            v-for="btn in globalBtns"
            :key="btn.name"
            :icon="btn.icon"
            :title="btn.label"
            v-debounce-click="() => handleGlobalButton(btn)"
          ></loading-icon>
        </template>
        <loading-icon
          v-if="globalIcons"
          data-cy="cs_table_refresh"
          :loading="loading"
          icon="el-icon-refresh"
          v-debounce-click="refreshTable"
          title="刷新"
        />
      </div>
    </div>
    <base-table
      :border="cfg.border"
      v-loading="loading"
      element-loading-background="rgba(255, 255, 255, 0.5)"
      element-loading-text="数据加载中"
      :columns="cfg.columns"
      :table-data="tableData"
      :operations="cfg.operations"
      :opt-col-width="cfg.optColWidth"
      :opt-fixed="cfg.optFixed"
      :row-key="cfg.rowKey"
      :expand-row-keys.sync="expandRowKeys"
      :lazy="cfg.lazy"
      :load="cfg.load"
      :default-expand-all="cfg.defaultExpandAll"
      :expand="!!$scopedSlots.expand"
      @refresh="refreshTable"
    >
      <template slot="cell" slot-scope="props">
        <slot name="cell" :row="props.row" :config="props.config" />
      </template>
      <template v-slot:expand="scope">
        <slot name="expand" v-bind="scope" />
      </template>
    </base-table>
    <el-pagination
      v-show="cfg.pagination && total > 0"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :current-page.sync="pageNum"
      :page-size.sync="pageSize"
      :page-sizes="[10, 20, 50, 100, 200]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
  </div>
</template>

<script>
import Log from "@/utils/log";
import permission from "./permission";
import Common from "./common";
import SearchForm from "./search";
import BaseTable from "./table";
import LoadingIcon from "./loading-icon.vue";
const log = Log("myTable");

function deepCopy(obj) {
  if (!obj && typeof obj !== "object") {
    throw new Error("error arguments");
  }
  const targetObj = Array.isArray(obj) ? [] : {};
  for (let key in obj) {
    //只对对象自有属性进行拷贝
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (obj[key] && typeof obj[key] === "object") {
        targetObj[key] = deepCopy(obj[key]);
      } else {
        targetObj[key] = obj[key];
      }
    }
  }
  return targetObj;
}

export default {
  name: "CsTable",
  mixins: [permission],
  components: { BaseTable, SearchForm, LoadingIcon },
  props: {
    className: {
      type: String,
      default: "",
    },
    // 列表标题，默认使用当前菜单做为标题，设置为flase时不显示标题
    title: {
      type: [Boolean, String],
      default: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    // 回传给页面的回调函数
    callback: {
      type: Object,
      default: () => ({}),
    },
    // 是否显示分页
    pagination: {
      type: Boolean,
      default: true,
    },

    // 列定义，包含显示的列和不显示的列。但不包含操作列
    columns: {
      type: Array,
      default: () => [],
    },

    // 操作列中的操作按钮定义，如果没有操作按钮，则不显示操作列
    operations: {
      type: Array,
      default: () => [],
    },

    // 列表右上角的全局按钮定义
    buttons: {
      type: Array,
      default: () => [],
    },

    // 获取列表数据
    getData: {
      type: Function,
      default: () => {
        window.alert("请指定getData参数");
      },
    },
  },
  data() {
    return {
      loading: true,
      // 是否显示全局刷新图标。当列表数据不是异步获取（一般是在页面中写的固定数据）时不显示
      refreshIcon: false,

      tableData: [], // 数据列表
      total: 0, // 数据总条数
      pageNum: 1,
      pageSize: 20,
      // 上一次搜索的条件,刷新的时候会用到
      oldSearchParams: {},
      // 用户输入的搜索条件
      searchParams: {},
      expandRowKeys: [],
      cfg: {
        border: true,
        rowKey: undefined,
        lazy: undefined,
        load: undefined,
        defaultExpandAll: false,
        columns: this.columns,
        buttons: this.buttons,
        operations: this.operations,
        pagination: this.pagination,
        title: this.title,
        optColWidth: "auto",
        optFixed: false,
        callback: this.callback,
        getData: this.getData,
        icons: true,
        defaultShowMore: false,
        ...this.config,
      },
    };
  },
  computed: {
    showHeader() {
      if (this.TableTitle) {
        // 有标题的时候显示header
        return true;
      } else {
        if (this.globalBtns.length > 0 && this.searchCols.length === 0) {
          // 没有标题的时候，不存在搜索项的话，展示header
          return true;
        } else {
          // 没有标题的时候，存在搜索项的话按钮显示在搜索的图标区域，此时不展示header
          return false;
        }
      }
    },
    globalBtns() {
      console.log(
        "globalBtns",
        this.globalIcons,
        this.searchCols.length,
        this.globalIcons && this.searchCols.length === 0
          ? [{ name: "refresh", action: this.refreshTable }]
          : []
      );
      const buttons = this.cfg.buttons
        .filter((btn) => this.hasPermission(btn.permission))
        .map(({ ...btn }, index) => {
          if (index === 0) {
            btn.type = "primary";
          }
          if (!btn.label) {
            btn.label = Common.getText(btn.name);
          }
          if (!btn.icon) {
            btn.icon = Common.getIcon(btn.name);
          }
          return btn;
        });
      if (
        this.globalIcons &&
        this.searchCols.length === 0 &&
        buttons.length > 0
      ) {
        buttons.push({
          name: "refresh",
          label: Common.getText("refresh"),
          action: this.refreshTable,
        });
      }
      return buttons;
    },
    globalIcons() {
      //
      console.log("globalIcons", this.cfg.icons, this.refreshIcon);
      return this.cfg.icons && this.refreshIcon;
    },
    TableTitle() {
      if (!this.cfg.title) {
        return "";
      }

      if (this.cfg.title === true) {
        return this.$route.meta.title;
      }

      return this.cfg.title;
    },
    // 搜索列数组
    searchCols() {
      return this.cfg.columns.filter((col) => col.search);
    },
  },
  beforeDestroy() {
    this.clearSetTimeout();
  },
  created() {
    if (this.searchCols.length === 0) {
      this.refreshTable();
    }

    // 回填callback，页面可以调用refresh刷新table
    this.config.refresh = () => {
      this.refreshTable();
    };

    // 兼容旧版本的refresh
    this.callback.refresh = () => {
      this.refreshTable();
    };
  },
  methods: {
    // 检查执行结果，并判断是否要刷新列表
    checkAndRefreshTable(item, result) {
      if (item.refresh !== true) {
        // 如果不需要自动刷新，则返回
        return;
      }

      // 没有异步处理，直接刷新列表
      if (!result.then) {
        this.refreshTable();
        return;
      }

      // 处理结果是promise，需要等到异步处理完成后刷新列表
      result.then(() => this.refreshTable());
    },

    // 列表全局按钮的点击处理
    handleGlobalButton(btn) {
      if (btn.action) {
        if (btn.confirmMsg) {
          this.$confirm(btn.confirmMsg, "提示", {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }).then(() => {
            this.checkAndRefreshTable(btn, btn.action());
          });
        } else {
          this.checkAndRefreshTable(btn, btn.action());
        }
      }
    },

    // 刷新列表数据
    refreshTable() {
      this.searchParams = deepCopy(this.oldSearchParams);
      this.getTableData();
    },
    getTableData(params = this.searchParams) {
      const queryParams = deepCopy({
        ...params,
        pageNum: this.pageNum,
        pageSize: this.pageSize,
      });
      log.debug("refreshTable by", queryParams);
      // 请求前清除自动刷新
      this.clearSetTimeout();
      const result = this.cfg.getData(queryParams);
      if (Array.isArray(result)) {
        // 静态数据，不分页
        this.loading = false;
        this.refreshIcon = false;
        this.total = 0;
        this.tableData = result;
        log.debug("The result data is an arrary");
        return;
      }
      this.loading = true;
      this.refreshIcon = true;
      this.expandRowKeys = [];
      result
        .then((datas) => {
          if (Array.isArray(datas)) {
            // 后台返回的是数组，没有分页信息，因此前台不显示分页
            this.total = 0;
            this.tableData = datas;
            return;
          }

          const {
            pageSize = this.pageSize,
            pageNum = this.pageNum,
            updateTime = 0,
            total = 0,
            records,
          } = datas;
          log.debug("tableData:", total, pageSize, pageNum);
          this.total = total;
          this.pageSize = pageSize;
          this.pageNum = pageNum;
          this.tableData = records;
          this.setTimeoutRefresh(updateTime);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * 定时刷新数据,刷新使用上次查询的参数,但是不修改搜索栏已输入的查询信息
     */
    setTimeoutRefresh(updateTime) {
      this.clearSetTimeout();
      if (updateTime > 0) {
        this.timer = setTimeout(() => {
          this.getTableData(this.oldSearchParams);
        }, updateTime * 1000);
      }
    },

    clearSetTimeout() {
      if (this.timer !== undefined) {
        clearTimeout(this.timer);
        this.timer = undefined;
      }
    },

    // 搜索按钮的点击处理
    onSearch(params) {
      this.pageNum = 1; // 每次搜索都从第一页开始获取数据
      this.oldSearchParams = deepCopy(params);
      this.getTableData();
    },

    // 分页处理
    handleSizeChange(val) {
      this.pageNum = 1; // 改变每页显示的条数后从第一页开始获取数据
      this.pageSize = val;
      this.refreshTable();
    },
    handleCurrentChange(val) {
      this.pageNum = val;
      this.refreshTable();
    },
  },
};
</script>

<style lang="scss">
@import "~@/styles/variables.scss";
.mytable {
  & > .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    border-bottom: 1px solid #aaa;
  }
  .page-title {
    font-size: 18px;
    font-weight: bold;
    padding: 10px 0;
  }
  .search-bar {
    padding: 10px 0;
    display: flex;
    justify-content: space-between;

    .el-form-item {
      margin-bottom: 0;
    }
  }
  .el-dropdown {
    font-size: 12px;
  }
  .mytable-opt-cell {
    .cell {
      text-overflow: unset;
    }
  }
  .link-btn {
    display: inline-block;
    cursor: pointer;
    padding: 0 5px;
    color: $linkColor;
    word-break: keep-all;
    white-space: nowrap;
    &.disabled {
      color: #999;
      cursor: not-allowed;
    }
  }

  .el-button {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .el-link {
    display: inline;
  }

  .global-icons {
    display: flex;
    align-items: center;
    align-self: flex-start;
    height: 34px;
    & > .loading-icon + .loading-icon {
      margin-left: 10px;
    }
  }
  .el-table th {
    background-color: #efefef;
    border-bottom: 1px solid #ddd;
  }
  .mytable-opt-cell {
    text-align: right;
  }
  .el-pagination {
    text-align: right;
    padding: 8px 3px;
  }
}
</style>
